import { Injectable } from '@angular/core';
import { ExecutionDetailItem, ExecutionDetails, ParallelismMode } from '../models/execution-details.models';
import { hasSequentialBaseline, buildGlobalMissingBaselineMessage } from '../../utils/baseline-utils';

/* ---------- Tipos ---------- */
export interface ThreadTime {
  start_time: number;
  stop_time: number;
}

export interface EnergyReading {
  name: string;
  value: number;
}

export interface ParallelConfiguration {
  mode: ParallelismMode;
  ranks: number | null;
  cores: number | null;
  totalCores: number;
  axisValue: number;
  axisLabel: string;
  configKey: string;
}

export interface Measurement {
  region: string;
  sequence: number;
  lines: string;
  filename: string;
  input: string;
  workload: number;
  parallelism: ParallelConfiguration;
  times: {
    region: number;
    threads: number[];
  };
  imbalance: number;
  energyReadings: EnergyReading[];

  executionPercentage: number;
  timeVariation: number;
}

export interface ParserOutput {
  measurements: Measurement[];
  arguments: string[];
  sensors: string[];
  executionDetails: ExecutionDetails;
}

/* ---------- Service ---------- */
@Injectable({ providedIn: 'root' })
export class ParserService {
  private getSampledEnergySensors(data: Record<string, any>): Map<string, string> {
    const sensors = new Map<string, string>();

    Object.values(data).forEach(execData => {
      const sampled = execData?.sensors;
      if (!sampled || typeof sampled !== 'object') return;

      Object.keys(sampled).forEach(sampledName => {
        const prefix = 'rapl_sample-';
        if (!sampledName.toLowerCase().startsWith(prefix)) return;

        const backend = sampledName.slice(prefix.length);
        if (backend) sensors.set(`rapl-${backend}`, sampledName);
      });
    });

    return sensors;
  }

  private mergeIntervals(threadTimes: ThreadTime[]): ThreadTime[] {
    if (threadTimes.some(time =>
      !Number.isFinite(time.start_time) ||
      !Number.isFinite(time.stop_time) ||
      time.stop_time < time.start_time
    )) {
      return [];
    }

    const sorted = threadTimes
      .map(time => ({ ...time }))
      .sort((left, right) => left.start_time - right.start_time);

    const merged: ThreadTime[] = [];
    sorted.forEach(current => {
      const previous = merged[merged.length - 1];
      if (!previous || current.start_time > previous.stop_time) {
        merged.push(current);
      } else if (current.stop_time > previous.stop_time) {
        previous.stop_time = current.stop_time;
      }
    });
    return merged;
  }

  private getRegionTime(threadTimes: ThreadTime[]): number {
    return this.mergeIntervals(threadTimes).reduce(
      (total, interval) => total + interval.stop_time - interval.start_time,
      0
    );
  }

  private normalizePowerSamples(rawSamples: unknown): Array<[number, number]> {
    if (!Array.isArray(rawSamples) || rawSamples.length < 2) return [];

    const samples: Array<[number, number]> = [];
    for (const rawSample of rawSamples) {
      if (!Array.isArray(rawSample) || rawSample.length < 2) return [];
      if (typeof rawSample[0] !== 'number' || typeof rawSample[1] !== 'number') {
        return [];
      }

      const power = rawSample[0];
      const timestamp = rawSample[1];
      if (!Number.isFinite(power) || power < 0 || !Number.isFinite(timestamp)) {
        return [];
      }

      const previous = samples[samples.length - 1];
      if (previous && timestamp <= previous[1]) return [];
      samples.push([power, timestamp]);
    }

    const gaps = samples.slice(1).map((sample, index) => sample[1] - samples[index][1]);
    const orderedGaps = [...gaps].sort((left, right) => left - right);
    const middle = Math.floor(orderedGaps.length / 2);
    const samplePeriod = orderedGaps.length % 2
      ? orderedGaps[middle]
      : (orderedGaps[middle - 1] + orderedGaps[middle]) / 2;

    if (!Number.isFinite(samplePeriod) || samplePeriod <= 0) return [];
    if (gaps.some(gap => gap > samplePeriod * 3)) return [];

    return samples;
  }

  private integrateSampledPower(
    rawSamples: unknown,
    threadTimes: ThreadTime[]
  ): number | null {
    const samples = this.normalizePowerSamples(rawSamples);
    const intervals = this.mergeIntervals(threadTimes);
    if (samples.length < 2 || intervals.length === 0) return null;

    const gaps = samples.slice(1).map((sample, index) => sample[1] - samples[index][1]);
    const orderedGaps = [...gaps].sort((left, right) => left - right);
    const middle = Math.floor(orderedGaps.length / 2);
    const samplePeriod = orderedGaps.length % 2
      ? orderedGaps[middle]
      : (orderedGaps[middle - 1] + orderedGaps[middle]) / 2;
    const firstTimestamp = samples[0][1];
    const lastTimestamp = samples[samples.length - 1][1];

    if (intervals.some(interval =>
      interval.start_time < firstTimestamp - samplePeriod ||
      interval.stop_time > lastTimestamp + samplePeriod
    )) {
      return null;
    }

    const powerAt = (timestamp: number): number => {
      if (timestamp <= firstTimestamp) return samples[0][0];
      if (timestamp >= lastTimestamp) return samples[samples.length - 1][0];

      let low = 0;
      let high = samples.length - 1;
      while (high - low > 1) {
        const candidate = Math.floor((low + high) / 2);
        if (samples[candidate][1] <= timestamp) low = candidate;
        else high = candidate;
      }

      const [leftPower, leftTime] = samples[low];
      const [rightPower, rightTime] = samples[high];
      const ratio = (timestamp - leftTime) / (rightTime - leftTime);
      return leftPower + (rightPower - leftPower) * ratio;
    };

    let energy = 0;
    intervals.forEach(interval => {
      const knots = [
        interval.start_time,
        ...samples
          .map(([, timestamp]) => timestamp)
          .filter(timestamp =>
            timestamp > interval.start_time && timestamp < interval.stop_time
          ),
        interval.stop_time
      ];

      for (let index = 0; index < knots.length - 1; index += 1) {
        const start = knots[index];
        const stop = knots[index + 1];
        energy += ((powerAt(start) + powerAt(stop)) / 2) * (stop - start);
      }
    });

    return Number.isFinite(energy) && energy >= 0 ? energy : null;
  }

  private getEnergyReadingValue(
    execData: any,
    sensor: string,
    regionKey: string | number,
    threadTimes: ThreadTime[],
    sampledEnergySensors: Map<string, string>
  ): number | null {
    const sensorData = execData?.[sensor];
    if (sensorData && typeof sensorData === 'object' && !Array.isArray(sensorData)) {
      const value =
        sensorData[regionKey] ??
        sensorData[String(regionKey)] ??
        sensorData[Number(regionKey)];
      const rawValue = Array.isArray(value) ? value[0] : value;
      if (typeof rawValue === 'number' && Number.isFinite(rawValue) && rawValue >= 0) {
        return rawValue;
      }
    }

    if (String(regionKey) === '0') {
      const rawGlobalValue = Array.isArray(sensorData) ? sensorData[0] : sensorData;
      if (
        typeof rawGlobalValue === 'number' &&
        Number.isFinite(rawGlobalValue) &&
        rawGlobalValue > 0
      ) {
        return rawGlobalValue;
      }
    }

    const sampledSensor = sampledEnergySensors.get(sensor);
    return sampledSensor
      ? this.integrateSampledPower(execData?.sensors?.[sampledSensor], threadTimes)
      : 0;
  }

  private buildExecutionDetails(
    config: any,
    dataKeys: string[],
    data: Record<string, unknown>,
    argumentsArr: string[]
  ): ExecutionDetails {
    const command = typeof config?.command === 'string' && config.command.trim()
      ? config.command
      : null;
    const parallelismMode = this.getParallelismMode(dataKeys);
    const rankValues = parallelismMode === 'mpi' || parallelismMode === 'hybrid'
      ? this.getPlannedValues(command, ['--ranks'], dataKeys, data, 'ranks')
      : [];
    const coreValues = parallelismMode === 'omp' || parallelismMode === 'hybrid'
      ? this.getPlannedValues(command, ['-c', '--cores'], dataKeys, data, 'cores')
      : [];
    const inputValues = this.getPlannedValues(
      command,
      ['-i', '--input', '--inputs'],
      dataKeys,
      data,
      'input',
      argumentsArr
    );
    const repetitionValues = this.getPlannedValues(
      command,
      ['-r', '--repetitions', '--repetition'],
      dataKeys,
      data,
      'repetitions'
    );

    const summary: ExecutionDetailItem[] = [];
    this.addDetail(summary, 'Application', config?.pkg);
    this.addDetail(summary, 'Execution date', config?.execdate);
    this.addDetail(summary, 'Host', config?.hostname);
    this.addDetail(summary, 'Kernel', config?.kernel);
    const parallelismSummary = this.getParallelismSummaryLabel(parallelismMode);
    if (parallelismSummary) {
      this.addDetail(summary, 'Parallel model', parallelismSummary);
    }

    const flags: ExecutionDetailItem[] = [];
    this.addDetail(flags, 'Auto instrumentation', config?.auto_instrumentation);
    this.addDetail(flags, 'Disable hyperthread', config?.disable_hyperthread);
    this.addDetail(flags, 'Disable cores', config?.disable_cores);
    this.addDetail(flags, 'Discard OMP overhead', config?.discard_ompnt);
    this.addDetail(flags, 'Idle time', config?.idle_time);
    this.addDetail(flags, 'MPI runtime', config?.mpi_runtimer);

    return {
      summary,
      flags,
      command,
      parallelismMode,
      rankValues,
      coreValues,
      inputValues,
      repetitionValues
    };
  }

  private getParallelismMode(dataKeys: string[]): ParallelismMode {
    const hasRanks = dataKeys.includes('ranks');
    const hasCores = dataKeys.includes('cores');

    if (hasRanks && hasCores) {
      return 'hybrid';
    }
    if (hasRanks) {
      return 'mpi';
    }
    if (hasCores) {
      return 'omp';
    }
    return 'unknown';
  }

  private getParallelismSummaryLabel(mode: ParallelismMode): string | null {
    switch (mode) {
      case 'hybrid':
        return 'Hybrid (MPI + OpenMP)';
      case 'mpi':
        return 'MPI';
      case 'omp':
        return 'OpenMP';
      default:
        return null;
    }
  }

  private buildParallelConfiguration(
    keyData: Record<string, string>,
    mode: ParallelismMode
  ): ParallelConfiguration {
    const parseOptionalNumber = (value: string | undefined): number | null => {
      if (value === undefined || value === '') {
        return null;
      }
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };

    const ranks = parseOptionalNumber(keyData['ranks']);
    const cores = parseOptionalNumber(keyData['cores']);

    if (mode === 'hybrid') {
      const totalCores = (ranks ?? 0) * (cores ?? 0);
      return {
        mode,
        ranks,
        cores,
        totalCores,
        axisValue: totalCores,
        axisLabel: `${totalCores} (${ranks ?? 0}x${cores ?? 0})`,
        configKey: `hybrid:${ranks ?? 0}x${cores ?? 0}`
      };
    }

    if (mode === 'mpi') {
      const effectiveRanks = ranks ?? 0;
      return {
        mode,
        ranks: effectiveRanks,
        cores: null,
        totalCores: effectiveRanks,
        axisValue: effectiveRanks,
        axisLabel: String(effectiveRanks),
        configKey: `mpi:${effectiveRanks}`
      };
    }

    const effectiveCores = cores ?? 0;
    return {
      mode: mode === 'unknown' ? 'omp' : mode,
      ranks: null,
      cores: effectiveCores,
      totalCores: effectiveCores,
      axisValue: effectiveCores,
      axisLabel: String(effectiveCores),
      configKey: `omp:${effectiveCores}`
    };
  }

  private getPlannedValues(
    command: string | null,
    flagNames: string[],
    dataKeys: string[],
    data: Record<string, unknown>,
    dataKey: string,
    fallback: string[] = []
  ): string[] {
    const fromCommand = command ? this.getCommandFlagValues(command, flagNames) : [];
    if (fromCommand.length > 0) {
      return fromCommand;
    }

    if (fallback.length > 0) {
      return fallback.map(value => String(value));
    }

    return this.getDataKeyValues(dataKeys, data, dataKey);
  }

  private getCommandFlagValues(command: string, flagNames: string[]): string[] {
    const escapedFlags = flagNames.map(flag => flag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const pattern = new RegExp(`(?:^|\\s)(?:${escapedFlags})(?:=|\\s+)("[^"]*"|'[^']*'|[^\\s]+)`);
    const match = command.match(pattern);
    if (!match) {
      return [];
    }

    const rawValue = match[1].replace(/^["']|["']$/g, '');
    return rawValue
      .split(',')
      .map(value => value.trim())
      .filter(value => value.length > 0);
  }

  private getDataKeyValues(dataKeys: string[], data: Record<string, unknown>, dataKey: string): string[] {
    const index = dataKeys.indexOf(dataKey);
    if (index === -1 || !data) {
      return [];
    }

    const seen = new Set<string>();
    Object.keys(data).forEach(key => {
      const value = key.split(';')[index];
      if (value !== undefined && value !== '' && !seen.has(value)) {
        seen.add(value);
      }
    });

    return Array.from(seen);
  }

  private addDetail(items: ExecutionDetailItem[], label: string, value: unknown): void {
    const formatted = this.formatDetailValue(value);
    if (formatted) {
      items.push({ label, value: formatted });
    }
  }

  private formatDetailValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (Array.isArray(value)) {
      return value
        .map(item => this.formatDetailValue(item))
        .filter(item => item.length > 0)
        .join(', ');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  }

  /**
   * Converte o JSON gerado pelo Analyzer para o formato do Viewer,
   */
  convertAnalyzer2Viewer(pascalData: any): ParserOutput {
    // --- Funções auxiliares ----------------------
    const tempGetArguments = (command: string, args: string[]): string[] => {
      // Captura posição de cada argumento na string do comando
      const positions = args.map(a => ({ pos: command.indexOf(a), arg: a }))
                            .filter(p => p.pos !== -1)
                            .sort((a, b) => a.pos - b.pos);
      return positions.map(p => p.arg);
    };

    const getKeyData = (keysLst: string[], keysStr: string): Record<string, string> => {
      const out: Record<string, string> = {};
      keysStr.split(';').forEach((val, idx) => out[keysLst[idx]] = val);
      return out;
    };

    const getProcElementValues = (keysLst: string[], dataLst: any[]): Record<string, any> => {
      const out: Record<string, any> = {};
      dataLst.forEach((val, idx) => out[keysLst[idx]] = val);
      return out;
    };

    // --- Validação e pré-processamento -----------
    if (!pascalData || !pascalData.data) {
      throw new Error('O JSON não possui a chave "data" válida.');
    }

    const oldVersion = !!pascalData.config?.data_descriptor?.values?.total_time;
    const dataKeys: string[] = pascalData.config?.data_descriptor?.keys ?? pascalData.config?.keys ?? [];
    const parallelismMode = this.getParallelismMode(dataKeys);

    /* argumentos podem vir em ordem trocada — reproduzimos a heurística original  */
    const command = typeof pascalData.config?.command === 'string' ? pascalData.config.command : '';
    const configArguments = Array.isArray(pascalData.config?.arguments) ? pascalData.config.arguments : [];
    let argumentsArr = this.getCommandFlagValues(command, ['-i', '--input', '--inputs']);
    if (argumentsArr.length === 0) {
      try {
        argumentsArr = tempGetArguments(command, configArguments);
      } catch {
        argumentsArr = configArguments;
      }
    }
    const executionDetails = this.buildExecutionDetails(
      pascalData.config,
      dataKeys,
      pascalData.data,
      argumentsArr
    );

    /* extras: regions & imbalance ---------------- */
    const extras = pascalData.config.data_descriptor.extras ?? {};
    const regionsDataKeys: string[] = extras.regions?.values ?? [];
    const hasImbalance = !!extras.imbalances;
    const mappedEnergySensors = Object.keys(extras)
      .filter(key => key.toLowerCase().startsWith('rapl'));
    const sampledEnergySensors = this.getSampledEnergySensors(pascalData.data);
    const energySensors = Array.from(new Set([
      ...mappedEnergySensors,
      ...sampledEnergySensors.keys()
    ]));

    // --- Conversão principal ---------------------
    const measurements: Measurement[] = [];

    Object.entries(pascalData.data).forEach(([keys, execData]: [string, any]) => {
      const keyData = getKeyData(dataKeys, keys);
      const parallelism = this.buildParallelConfiguration(keyData, parallelismMode);
      const argumentStr = argumentsArr[Number(keyData['input'])];

      /* — Whole-program (region "0") — */
      const totalTime = oldVersion
        ? Number(execData.total_time)
        : Number(execData.stop_time) - Number(execData.start_time);
      const totalInterval = oldVersion
        ? []
        : [{
            start_time: Number(execData.start_time),
            stop_time: Number(execData.stop_time)
          }];

      const totalEnergyReadings = energySensors
        .map(sensor => ({
          name: sensor,
          value: this.getEnergyReadingValue(
            execData,
            sensor,
            0,
            totalInterval,
            sampledEnergySensors
          )
        }))
        .filter((reading): reading is EnergyReading => reading.value !== null);
        

      measurements.push({
        region: '0',
        sequence: 0,
        lines: '0, 0',
        filename: 'whole program',
        input: argumentStr,
        workload: Number(keyData['input']),
        parallelism,
        times: { region: totalTime, threads: [totalTime] },
        imbalance: 0,
        energyReadings: totalEnergyReadings,
        executionPercentage: 100,
        timeVariation: 0
      });

      /* — Sub-regiões (apenas V2) — */
      if (!oldVersion && execData.regions) {
        const parentTime = totalTime; // em segundos

        Object.entries(execData.regions).forEach(([regionId, regionArray], idx) => {
          // times por thread
          const threadTimes: ThreadTime[] = (regionArray as any[]).map((row: any[]) => {
            const vals = getProcElementValues(regionsDataKeys, row);
            return { start_time: Number(vals['start_time']), stop_time: Number(vals['stop_time']) };
          });

          const regionTime = this.getRegionTime(threadTimes);
          const info = getProcElementValues(regionsDataKeys, (regionArray as any[])[0]);
          const lines = `${info['start_line']}, ${info['stop_line']}`;

          const imbalanceVal = hasImbalance && execData.imbalances?.[regionId]
            ? Number(execData.imbalances[regionId])
            : 0;

          const energyReadings = energySensors
            .map(sensor => ({
              name: sensor,
              value: this.getEnergyReadingValue(
                execData,
                sensor,
                regionId,
                threadTimes,
                sampledEnergySensors
              )
            }))
            .filter((reading): reading is EnergyReading => reading.value !== null);

          const execPct = parentTime > 0 ? (regionTime / parentTime) * 100 : 0;
          const timeVarPct = imbalanceVal * 100;

          measurements.push({
            region: `0.${regionId}`,
            sequence: idx + 1,
            lines,
            filename: info['filename'],
            input: argumentStr,
            workload: Number(keyData['input']),
            parallelism,
            times: {
              region: regionTime,
              threads: threadTimes.map(t => t.stop_time - t.start_time)
            },
            imbalance: imbalanceVal,
            energyReadings,
            executionPercentage: execPct,
            timeVariation: timeVarPct
          });
        });
      }
    });

    if (!hasSequentialBaseline(measurements)) {
      throw new Error(buildGlobalMissingBaselineMessage(parallelismMode, executionDetails.command));
    }

    return { measurements, arguments: argumentsArr, sensors: energySensors, executionDetails };
  }
}
